import { UuidString } from '~src/utils/UUID';
import { GroupMemberArray, GroupArray, CreateGroupResponse } from '~/src/types/groups';
import { KyInstance, type Options } from 'ky';

export class GroupClient {
  private http_client: KyInstance;
  constructor(http_client: KyInstance) {
    this.http_client = http_client;
  }

  async getGroupMembers(group_id: UuidString, config: Options = {}): Promise<GroupMemberArray> {
    const res = (await this.http_client
      .get('groups/members', {
        searchParams: {
          group_id: group_id.toString(),
        },
        ...config,
      })
      .json()) as { members: GroupMemberArray };

    return GroupMemberArray.parse(res.members);
  }

  async createGroup(opts: CreateGroupOptions, config: Options = {}): Promise<UuidString> {
    const res = CreateGroupResponse.parse(await this.http_client.post('groups', { json: opts, ...config }).json());

    return res.group_id;
  }

  async joinGroup(opts: JoinGroupOptions, config: Options = {}) {
    const res = (await this.http_client.post('groups/join', { json: opts, ...config }).json()) as {
      groups: GroupArray;
    };

    return;
  }

  async getGroupInfo(config: Options = {}): Promise<GroupArray> {
    const res = (await this.http_client.get('groups', config).json()) as { groups: GroupArray };
    return GroupArray.parse(res.groups);
  }

  async deleteGroup(group_id: UuidString, config: Options = {}): Promise<void> {
    await this.http_client.delete('groups', {
      searchParams: {
        group_id: group_id.toString(),
      },
      ...config,
    });
  }

  async leaveGroup(group_id: UuidString, config: Options = {}): Promise<void> {
    await this.http_client.post('groups/leave', {
      json: { group_id: group_id },
      ...config,
    });
  }

  async createInvite(group_id: UuidString, config: Options = {}): Promise<string> {
    const res = (await this.http_client
      .post('groups/invite', {
        json: { group_id: group_id },
        ...config,
      })
      .json()) as { token: string };
    return res.token;
  }

  async updateProfile(opts: UpdateProfileOptions, config: Options = {}): Promise<void> {
    await this.http_client.patch('groups/profile', {
      json: {
        group_id: opts.group_id,
        alias: opts.alias,
      },
      ...config,
    });
  }

  async updateGroupSettings(opts: UpdateGroupSettingsOptions, config: Options = {}): Promise<void> {
    await this.http_client.patch('groups/settings', {
      json: {
        group_id: opts.group_id,
        display_name: opts.display_name,
      },
      ...config,
    });
  }
}

export interface CreateGroupOptions {
  display_name: string;
  user_alias: string;
}

export interface JoinGroupOptions {
  token: string;
  user_alias: string;
}

export interface UpdateProfileOptions {
  group_id: UuidString;
  alias?: string;
}

export interface UpdateGroupSettingsOptions {
  group_id: UuidString;
  display_name?: string;
}
